'use client';

import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { GTMAnalyticsProps } from './GTMAnalytics.types';
const GTMAnalytics: React.FC<GTMAnalyticsProps> = ({
  gtmId
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [lcpFinished, setLcpFinished] = useState(false);

  // This effect sets up the dataLayer proxy immediately on mount.
  useEffect(() => {
    if (window.dataLayerProxy) {
      return;
    }
    const dataLayer = (window as any).dataLayer = (window as any).dataLayer || [];
    window.dataLayerProxy = true;

    // Create a proxy 'push' function that logs events.
    dataLayer.push = function (...args: any[]) {
      Array.prototype.push.apply(dataLayer, args);
    };

    // Queue Facebook Pixel calls if fbq is not defined
    if (!(window as any).fbq) {
      const fbqQueue: any[] = [];
      (window as any).fbq = function (...args: any[]) {
        fbqQueue.push(args);
      };

      // Store the queue reference so it can be replayed later
      (window as any).fbq.queue = fbqQueue;
      (window as any).fbq.loaded = false;
    }
  }, [pathname]);

  // Effect to monitor LCP completion
  useEffect(() => {
    if (!gtmId) {
      return;
    }
    let lcpStabilizeTimeout: NodeJS.Timeout;
    let observer: PerformanceObserver;

    // Set up PerformanceObserver to monitor LCP
    if ('PerformanceObserver' in window) {
      observer = new PerformanceObserver(list => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry && lastEntry.startTime > 0) {
          // Clear previous timeout
          clearTimeout(lcpStabilizeTimeout);

          // Wait for LCP to stabilize (no new entries for 200ms)
          lcpStabilizeTimeout = setTimeout(() => {
            setLcpFinished(true);
            observer.disconnect();
          }, 200);
        }
      });
      try {
        observer.observe({
          type: 'largest-contentful-paint',
          buffered: true
        });
      } catch (error) {
        // Fallback if LCP observation fails
        setLcpFinished(true);
      }
    } else {
      // Fallback for browsers without PerformanceObserver support
      // Use a reasonable timeout-based approach
      setTimeout(() => {
        setLcpFinished(true);
      }, 2500); // Reasonable estimate for LCP completion
    }
    return () => {
      clearTimeout(lcpStabilizeTimeout);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [gtmId]);

  // Effect to inject GTM script once LCP is finished
  useEffect(() => {
    if (lcpFinished && gtmId) {
      const injectGtmScript = () => {
        if (window.gtmDidInit) {
          return;
        }
        window.gtmDidInit = true;
        (window as any).dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js'
        });
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
        document.head.appendChild(script);
      };
      injectGtmScript();
    }
  }, [lcpFinished, gtmId]);

  // Effect to track page views (this will also be queued if it runs early)
  useEffect(() => {
    if (gtmId && pathname) {
      try {
        const searchString = searchParams.toString();
        const url = `${pathname}${searchString ? `?${searchString}` : ''}`;

        // Validate URL construction
        new URL(url, window.location.origin);
        (window as any).dataLayer.push({
          event: 'pageview',
          page: url,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        // Fallback: just use pathname
        (window as any).dataLayer.push({
          event: 'pageview',
          page: pathname || '/',
          timestamp: new Date().toISOString()
        });
      }
    }
  }, [pathname, searchParams, gtmId]);
  return <>
      {/* GTM NoScript fallback */}
      <noscript>
        <iframe src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`} height="0" width="0" style={{
        display: 'none',
        visibility: 'hidden'
      }} />
      </noscript>
    </>;
};
declare global {
  interface Window {
    gtmDidInit: boolean;
    dataLayerProxy: boolean;
  }
}
export default GTMAnalytics;